import { nextTick } from 'vue';

import { logger } from '@/plugins/logger';
import { capitalize } from '@/utils/helpers/string-utils';

export default {
  namespaced: true,

  state: () => ({
    item: {},
    colors: [],
    activeColor: '',
    loading: false,
    loadingCounter: 0,
    availability: [],
    availabilityProductsCount: [],
    questionsCount: 0,
    promoKits: [],
    currentSkuId: null,
    personalPrice: null,
    currentSizeName: '',
    availabilityPoints: [],
  }),

  getters: {
    item: (state) => state.item,
    colors: (state) => state.colors,
    activeColor: (state) => state.activeColor,
    sizes: (state) => state.item?.skus || [],
    personalPrice: (state) => state.personalPrice,
    loading: (state) => state.loading,
    availability: (state) => state.availability || [],
    availabilityProductsCount: (state) => state.availabilityProductsCount || [],
    availabilityPoints: (state) => state.availabilityPoints || [],
    questionsCount: (state) => state.questionsCount || 0,
    promoKits: (state) => state.promoKits || [],
    promoKitsCount: (_, getters) => getters.item.promoKitsCount,
    currentSku: (state, getters) => (getters.sizes.find((sku) => sku.available && sku.id === state.currentSkuId) || {}),
    currentSizeName: (state) => state.currentSizeName,
  },

  actions: {
    async loadProduct({
      state,
      commit,
    }, {
      productId,
    } = {}) {
      commit('loading');

      const [product, questionsCount] = await Promise.allSettled([
        this.$apiBFF.fetchProduct(productId),
        this.$apiBFF.getProductQuestionsCount({ productId }),
      ]);

      if (product.status === 'fulfilled') {
        commit('setProduct', product.value);
        this.$apiBFF.sendRecentlyViewedProduct(productId);
      } else {
        return Promise.reject(product.reason);
      }

      if (questionsCount.status === 'fulfilled') {
        commit('setQuestionsCount', questionsCount.value.totalCount);
      }

      if (!state.colors.length) {
        // первая загрузка карточки товара на ssr
        // сетим их отдельно, чтобы не прыгали при изменении
        const colors = [
          product.value.color,
          ...product.value.linkedColorModels.map((it) => it.color),
        ].map((c) => ({
          ...c,
          name: capitalize(c?.name ?? ''),
        }));

        commit('setColors', colors);
        commit('setActiveColor', product.value?.id);
      }

      commit('loading', false);
    },

    chooseColor({ commit, dispatch }, {
      productId,
    }) {
      commit('setActiveColor', productId);

      return Promise.allSettled([
        dispatch('loadProduct', { productId }),
        dispatch('loadAvailabilityList', { productId }),
      ]);
    },

    async loadAvailabilityList({ commit }, { productId } = {}) {
      return this.$apiBFF.fetchAvailabilityStores({ productId })
        .then((data) => {
          commit('setAvailableList', data);
        })
        .catch(() => {
          commit('setAvailableList', []);
        });
    },

    async loadAvailabilityProductsCount({ commit }, { productId } = {}) {
      return this.$apiBFF.fetchAvailabilityProductsCount({ productId })
        .then((data) => {
          commit('setAvailabilityProductsCount', data);
        })
        .catch(() => {
          commit('setAvailabilityProductsCount', []);
        });
    },

    async loadPersonalPrice({ commit }, { productId } = {}) {
      try {
        commit('loading');
        const prices = await this.$apiBFF.fetchPersonalPrice(productId);
        commit('setPersonalPrice', prices);
      } catch (e) {
        logger.error(e);
        commit('setPersonalPrice', null);
      } finally {
        commit('loading', false);
      }
    },

    async loadAvailabilityPoints({ commit }, { skuId } = {}) {
      if (!skuId) return commit('setAvailabilityPoints', []);

      try {
        commit('loading');
        const points = await this.$apiBFF.fetchPickupAvailability(skuId);
        commit('setAvailabilityPoints', points);
      } catch (e) {
        logger.error(e);
        commit('setAvailabilityPoints', []);
      } finally {
        // небольшой таймаут что бы зарезолвился computed
        await nextTick();

        commit('loading', false);
      }
    },
  },

  mutations: {
    setProduct: (state, product = {}) => {
      state.item = {
        ...product,
        color: {
          ...product.color,
          name: capitalize(product.color.name),
        },
      };
    },

    setCurrentSkuId: (state, skuId) => {
      state.currentSkuId = skuId;
    },

    setQuestionsCount: (state, questionsCount = 0) => {
      state.questionsCount = questionsCount;
    },

    setColors: (state, colors = []) => {
      state.colors = colors;
    },

    setActiveColor: (state, color = {}) => {
      state.activeColor = color;
    },

    setPersonalPrice: (state, prices) => {
      state.personalPrice = prices;
    },

    setCurrentSizeName: (state, size) => {
      state.currentSizeName = size;
    },

    loading: (state, loading = true) => {
      state.loadingCounter += loading ? 1 : -1;
      state.loading = state.loadingCounter > 0;
    },

    setAvailableList: (state, payload) => {
      state.availability = payload;
    },

    setAvailabilityProductsCount: (state, payload) => {
      state.availabilityProductsCount = payload;
    },

    setAvailabilityPoints: (state, payload) => {
      state.availabilityPoints = payload;
    },
  },
};
